import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Coins,
  ShieldCheck,
  Eye,
  Scale,
  FileText,
  Users,
  ClipboardCheck,
  MessagesSquare,
  Layers,
  Sparkles,
  Vault,
  Award,
  FileSearch,
  Building2,
  HandCoins,
  Boxes,
} from 'lucide-react';
import { colors } from '../styles/colors';
import { useFillout } from '../hooks/useFillout';

const SectionHeading = ({ eyebrow, title, sub, align = 'center', maxW = 'max-w-3xl' }) => (
  <div className={`${maxW} ${align === 'center' ? 'mx-auto text-center' : ''} mb-12 sm:mb-16`}>
    {eyebrow && (
      <div className={`flex items-center gap-3 mb-4 ${align === 'center' ? 'justify-center' : ''}`}>
        <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />
        <span className="text-[11px] uppercase tracking-[0.28em] font-medium" style={{ color: colors.teal }}>{eyebrow}</span>
        {align === 'center' && <span className="h-px w-8" style={{ backgroundColor: colors.teal }} />}
      </div>
    )}
    <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight leading-tight" style={{ color: colors.white }}>{title}</h2>
    {sub && <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{sub}</p>}
  </div>
);

const BullionProvidersPage = () => {
  const [heroVisible, setHeroVisible] = useState(false);
  const navigate = useNavigate();
  const { openFilloutPopup } = useFillout();

  useEffect(() => {
    const raf = requestAnimationFrame(() => setHeroVisible(true));
    window.scrollTo(0, 0);
    return () => cancelAnimationFrame(raf);
  }, []);

  // §4 participation flow
  const participationSteps = [
    { step: '01', title: 'Initial Discussion', body: 'Review holdings and participation objectives.', Icon: MessagesSquare },
    { step: '02', title: 'Asset Review', body: 'Verification and eligibility assessment.', Icon: FileSearch },
    { step: '03', title: 'Reserve Contribution', body: 'Approved bullion enters the reserve framework.', Icon: Vault },
    { step: '04', title: 'Reserve Participation', body: 'Assets contribute to the broader reserve ecosystem.', Icon: Layers },
  ];

  // §5 participation cards
  const participation = [
    { Icon: HandCoins, title: 'Reserve Contribution', body: 'Contribute physical assets into a transparent reserve environment.' },
    { Icon: ShieldCheck, title: 'Verification', body: 'Operate within a framework built around asset verification.' },
    { Icon: Eye, title: 'Reserve Visibility', body: 'Participate in a reserve supported by reporting and transparency.' },
    { Icon: Scale, title: 'Governance', body: 'Benefit from clearly defined participant roles and governance structures.' },
    { Icon: Users, title: 'Ecosystem Participation', body: 'Become part of a broader reserve ecosystem connecting assets and capital.' },
  ];

  // §6 suitability criteria
  const suitability = [
    { Icon: Sparkles, label: 'Gold Bullion' },
    { Icon: Award, label: 'Silver Bullion' },
    { Icon: ShieldCheck, label: 'Verification Standards' },
    { Icon: FileText, label: 'Documentation Quality' },
    { Icon: Boxes, label: 'Storage Arrangements' },
    { Icon: Layers, label: 'Reserve Suitability' },
  ];

  // §8 governance
  const governance = [
    { Icon: ShieldCheck, title: 'Verification Standards', body: 'Defined verification standards supporting reserve integrity.' },
    { Icon: FileSearch, title: 'Asset Review Process', body: 'Independent review of physical bullion holdings and documentation.' },
    { Icon: FileText, title: 'Reserve Reporting', body: 'Ongoing visibility through reserve composition and reporting.' },
    { Icon: Scale, title: 'Governance Separation', body: 'Corporate, reserve, and marketplace layers operate distinctly.' },
    { Icon: Users, title: 'Ecosystem Partners', body: 'Defined participant roles across the broader ecosystem.' },
  ];

  // §9 engagement
  const engagementSteps = [
    { step: '01', title: 'Discuss Holdings', body: 'Understand the bullion holdings and participation objectives.', Icon: MessagesSquare },
    { step: '02', title: 'Review Eligibility', body: 'Assess documentation and asset suitability.', Icon: FileSearch },
    { step: '03', title: 'Assess Reserve Participation', body: 'Review reserve contribution and participation pathways.', Icon: Layers },
    { step: '04', title: 'Begin Structured Engagement', body: 'Maintain communication, reporting, and reserve visibility.', Icon: FileText },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* §1 HERO */}
      <section className="relative overflow-hidden" style={{ minHeight: '92vh', background: 'linear-gradient(180deg, #000000 0%, #03090C 60%, #07141A 100%)' }}>
        <picture>
          <source srcSet="/lp-hero-reserves.webp" type="image/webp" />
          <img
            src="/lp-hero-reserves.jpg"
            alt=""
            aria-hidden="true"
            data-testid="bullion-hero-image"
            className={`absolute inset-0 w-full h-full object-cover bullion-hero-drift transition-opacity duration-[2000ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`}
            style={{ objectPosition: 'center center' }}
            loading="eager"
            decoding="async"
          />
        </picture>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.28) 35%, rgba(0,0,0,0.45) 70%, rgba(0,0,0,0.92) 100%)' }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(55% 60% at 50% 55%, rgba(0,128,128,0.10) 0%, rgba(0,0,0,0) 70%)' }} />

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center min-h-[92vh] pt-32 pb-20 sm:pt-36 sm:pb-24">
          <div className={`transition-all duration-1000 ease-out transform ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`} style={{ fontFamily: "'Montserrat', system-ui, sans-serif" }}>
            <div className="flex items-center gap-3 mb-7 justify-center">
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] font-medium" style={{ color: colors.teal, fontFamily: 'inherit' }}>
                <Coins className="w-3.5 h-3.5" strokeWidth={1.5} />
                For Bullion Providers
              </span>
              <span className="h-px w-10" style={{ backgroundColor: colors.teal, opacity: 0.7 }} />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[60px] leading-[1.05] tracking-[-0.02em] mb-7 max-w-4xl mx-auto" style={{ color: colors.white, fontFamily: 'inherit', fontWeight: 700 }}>
              CONTRIBUTE PHYSICAL BULLION TO A TRANSPARENT RESERVE FRAMEWORK.
            </h1>
            <p className="text-base sm:text-lg lg:text-xl leading-[1.55] max-w-2xl mx-auto mb-12" style={{ color: 'rgba(255,255,255,0.72)', fontFamily: 'inherit' }}>
              Arizore enables eligible bullion providers to participate in a reserve ecosystem built around transparency, verification, and structured participation.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
              <button
                onClick={openFilloutPopup}
                data-testid="bullion-hero-discuss-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}`, fontFamily: 'inherit' }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                Discuss Your Holdings
                <ArrowRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => navigate('/reserve-architecture')}
                data-testid="bullion-hero-framework-btn"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)', fontFamily: 'inherit' }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
              >
                Understand The Reserve Framework
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
        <style>{`
          @keyframes bullion-hero-drift { 0% { transform: scale(1.04) translate3d(0,0,0); } 50% { transform: scale(1.06) translate3d(-0.5%, -0.5%, 0); } 100% { transform: scale(1.04) translate3d(0,0,0); } }
          .bullion-hero-drift { animation: bullion-hero-drift 24s ease-in-out infinite; will-change: transform; }
          @media (prefers-reduced-motion: reduce) { .bullion-hero-drift { animation: none; } }
        `}</style>
      </section>

      {/* §2 BULLION PARTICIPATION OPPORTUNITY */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/home-bg-grid.webp)', backgroundSize: 'cover', backgroundPosition: 'center 20%', opacity: 0.1 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.65) 50%, #040404 100%)' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="The Bullion Participation Opportunity"
            title="PHYSICAL ASSETS CAN CONTRIBUTE TO MORE THAN STORAGE."
            sub="Physical bullion is recognised globally as a valuable asset. However, many holdings remain isolated from broader reserve participation frameworks. Arizore was created to help connect eligible physical assets with a transparent reserve ecosystem designed around visibility, participation, and long-term reserve growth."
          />
          <div className="max-w-2xl mx-auto rounded-2xl p-6 sm:p-8 text-center" style={{ background: 'linear-gradient(180deg, rgba(0,128,128,0.07) 0%, rgba(0,128,128,0.02) 100%)', border: `1px solid ${colors.teal}26` }}>
            <p className="text-base sm:text-lg leading-relaxed" style={{ color: colors.white, opacity: 0.85 }}>
              The opportunity is not <span style={{ color: colors.white, opacity: 0.55 }}>ownership</span>. The opportunity is <span style={{ color: colors.teal, fontWeight: 500 }}>participation</span>.
            </p>
          </div>
        </div>
      </section>

      {/* §3 WHY ARIZORE EXISTS */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.14 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.55) 50%, #050505 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Why Arizore Exists"
            title="CONNECTING PHYSICAL ASSETS TO THE RESERVE ECOSYSTEM."
            sub="The reserve framework provides a structured environment where eligible bullion assets may contribute to the reserve and participate within a transparent ecosystem."
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
            {[
              { Icon: ShieldCheck, label: 'Verification' },
              { Icon: Eye, label: 'Transparency' },
              { Icon: Layers, label: 'Reserve Visibility' },
              { Icon: FileText, label: 'Reporting' },
              { Icon: Users, label: 'Ecosystem Participation' },
            ].map(({ Icon, label }) => (
              <div
                key={label}
                data-testid={`bullion-why-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex flex-col items-center text-center p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.12em] leading-tight" style={{ color: colors.white }}>{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §4 HOW PARTICIPATION WORKS */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl px-6 sm:px-10 lg:px-16 py-14 sm:py-16 lg:py-20"
            style={{
              background: 'linear-gradient(180deg, rgba(255,255,255,0.025) 0%, rgba(255,255,255,0.01) 100%)',
              border: '1px solid rgba(255,255,255,0.06)',
              boxShadow: '0 30px 60px -30px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
            }}
          >
            <SectionHeading
              eyebrow="How Participation Works"
              title="A STRUCTURED RESERVE CONTRIBUTION PROCESS."
              sub="Participation follows a deliberate review process designed for trust, verification, and transparency."
            />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6 mb-12">
              {participationSteps.map(({ step, title, body, Icon }, idx) => (
                <div
                  key={step}
                  data-testid={`bullion-participation-step-${step}`}
                  className="relative p-6 sm:p-7 rounded-2xl"
                  style={{ background: 'rgba(255,255,255,0.025)', border: `1px solid ${colors.teal}24` }}
                >
                  <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full flex items-center justify-center text-xs font-semibold" style={{ backgroundColor: '#070707', color: colors.teal, border: `1px solid ${colors.teal}80` }}>{step}</span>
                  <div className="mt-3 mx-auto w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                    <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                  </div>
                  <h3 className="text-sm font-semibold uppercase tracking-[0.12em] mb-2 text-center" style={{ color: colors.white }}>{title}</h3>
                  <p className="text-sm leading-relaxed text-center" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
                  {idx < 3 && <ArrowRight aria-hidden="true" className="hidden lg:block absolute top-1/2 -right-4 w-5 h-5 -translate-y-1/2" style={{ color: colors.teal, opacity: 0.4 }} strokeWidth={1.5} />}
                </div>
              ))}
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => navigate('/reserve-architecture')}
                data-testid="bullion-participation-cta"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
                style={{ backgroundColor: 'transparent', color: colors.teal, border: `1px solid ${colors.teal}` }}
                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.teal}14`; e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`; }}
                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                Explore Reserve Architecture
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* §5 WHY PROVIDERS PARTICIPATE */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.1 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.6) 50%, #050505 100%)' }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading eyebrow="Why Providers Participate" title="BUILT AROUND TRANSPARENCY AND PARTICIPATION." sub="The reserve framework is designed to support transparent participation, verification, and reserve visibility." />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {participation.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`bullion-participation-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="group p-7 sm:p-8 rounded-2xl h-full flex flex-col transition-all duration-500"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${colors.teal}40`; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = `0 20px 40px -20px ${colors.teal}40`; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.07)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: `${colors.teal}14`, border: `1px solid ${colors.teal}40` }}>
                  <Icon className="w-6 h-6" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-3 leading-tight tracking-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-sm sm:text-base leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* §6 ELIGIBILITY */}
      <section className="py-20 sm:py-28" style={{ background: 'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Eligibility Matters"
            title="PARTICIPATION BEGINS WITH VERIFICATION."
            sub="Participation depends on asset quality, documentation standards, reserve suitability, and verification requirements. The following are review criteria, not guarantees of acceptance."
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 sm:gap-4 mb-10">
            {suitability.map(({ Icon, label }) => (
              <div
                key={label}
                data-testid={`bullion-suitability-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className="flex flex-col items-center text-center p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <span className="text-xs font-semibold uppercase tracking-[0.12em] leading-tight" style={{ color: colors.white }}>{label}</span>
              </div>
            ))}
          </div>
          <p className="max-w-2xl mx-auto text-center text-xs sm:text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.45 }}>
            Review criteria are indicative only. Final participation depends on independent verification and reserve framework requirements.
          </p>
        </div>
      </section>

      {/* §8 GOVERNANCE & VERIFICATION */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#050505' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-layers.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.12 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #050505 0%, rgba(5,5,5,0.6) 50%, #050505 100%)' }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="Governance & Verification"
            title="TRUST THROUGH STRUCTURE."
            sub="Reserve participation is supported through verification standards, governance frameworks, reporting processes, and ecosystem partners."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4 mb-12">
            {governance.map(({ Icon, title, body }) => (
              <div
                key={title}
                data-testid={`bullion-governance-${title.toLowerCase().replace(/\s+/g, '-')}`}
                className="p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: `${colors.teal}12`, border: `1px solid ${colors.teal}30` }}>
                  <Icon className="w-5 h-5" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <h3 className="text-sm font-semibold uppercase tracking-[0.12em] mb-2 leading-tight" style={{ color: colors.white }}>{title}</h3>
                <p className="text-xs sm:text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/reserve-data/azg')}
              data-testid="bullion-governance-cta-reserve-data"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-lg text-sm font-semibold uppercase tracking-[0.14em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.teal, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${colors.teal}14`; e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.teal}1f`; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              View Reserve Data
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* §9 HOW ENGAGEMENT BEGINS */}
      <section className="py-20 sm:py-28" style={{ backgroundColor: '#040404' }}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            eyebrow="How Engagement Begins"
            title="START THE CONVERSATION."
            sub="Engagement follows a deliberate, consultative process. This is a structured review, not a sales funnel."
          />
          <ol className="relative space-y-3">
            <div aria-hidden="true" className="absolute left-7 sm:left-8 top-7 bottom-7 w-px" style={{ background: `linear-gradient(180deg, transparent 0%, ${colors.teal}50 8%, ${colors.teal}50 92%, transparent 100%)` }} />
            {engagementSteps.map(({ step, title, body, Icon }) => (
              <li
                key={step}
                data-testid={`bullion-engagement-step-${step}`}
                className="relative flex items-start gap-5 sm:gap-6 p-5 sm:p-6 rounded-xl"
                style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)', border: '1px solid rgba(255,255,255,0.06)' }}
              >
                <div className="relative z-10 flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#070707', border: `1px solid ${colors.teal}50`, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.03), 0 0 0 4px rgba(4,4,4,1)' }}>
                  <Icon className="w-6 h-6 sm:w-7 sm:h-7" style={{ color: colors.teal }} strokeWidth={1.5} />
                </div>
                <div className="flex-grow pt-1">
                  <span className="block text-[10px] uppercase tracking-[0.28em] font-medium mb-1.5" style={{ color: colors.teal, opacity: 0.85 }}>Step {step}</span>
                  <h3 className="text-base sm:text-lg font-semibold tracking-tight mb-2" style={{ color: colors.white }}>{title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: colors.white, opacity: 0.65 }}>{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* §10 GET IN TOUCH */}
      <section className="relative py-20 sm:py-28 overflow-hidden" style={{ backgroundColor: '#040404' }}>
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ backgroundImage: 'url(/lp-bg-nodes.webp)', backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.09 }} />
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, #040404 0%, rgba(4,4,4,0.75) 50%, #040404 100%)' }} />

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <SectionHeading
            eyebrow="Get In Touch"
            title="DISCUSS YOUR HOLDINGS."
            sub="If you represent physical bullion holdings and would like to explore reserve participation opportunities, speak with the Arizore team."
          />
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={openFilloutPopup}
              data-testid="bullion-contact-discuss-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: colors.teal, color: colors.white, border: `1px solid ${colors.teal}` }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `0 0 0 4px ${colors.teal}26`; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Discuss Your Holdings
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/reserve-architecture')}
              data-testid="bullion-contact-framework-btn"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-lg text-sm font-semibold uppercase tracking-[0.16em] transition-all duration-300"
              style={{ backgroundColor: 'transparent', color: colors.white, border: '1px solid rgba(255,255,255,0.25)' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.45)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)'; }}
            >
              Understand The Reserve Framework
              <ArrowUpRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BullionProvidersPage;
